import type { StockItem } from '../types/stock'
import { isItemBelowReorderThreshold } from '../services/stockRestockService'

export interface StockLowCheckInput {
  quantity: number
  minThreshold?: number | null
  reorderPoint?: number | null
  byLocation?: Record<string, number | null | undefined> | null
  reorderPointByLocation?: Record<string, number | null | undefined> | null
}

/**
 * Checks whether a stock item is at or below its reorder threshold, either:
 * 1. Globally via `isItemBelowReorderThreshold` (`reorderPoint ?? minThreshold`), OR
 * 2. At any specific location when `reorderPointByLocation` has a finite numeric point
 *    and `(byLocation?.[loc] ?? 0) <= point`.
 */
export const isStockItemLow = (item: StockLowCheckInput | StockItem | null | undefined): boolean => {
  if (!item) return false
  if (isItemBelowReorderThreshold(item as StockItem)) {
    return true
  }
  const points = item.reorderPointByLocation
  if (points && typeof points === 'object') {
    for (const [loc, point] of Object.entries(points)) {
      if (typeof point === 'number' && Number.isFinite(point)) {
        const qtyAtLoc = item.byLocation?.[loc]
        const numericQty = typeof qtyAtLoc === 'number' && Number.isFinite(qtyAtLoc) ? qtyAtLoc : 0
        if (numericQty <= point) {
          return true
        }
      }
    }
  }
  return false
}

/**
 * Returns the effective threshold and shortfall for a low-stock item so leaderboards
 * and alerts can sort by the worst shortfall across global or per-location thresholds.
 */
export const getStockShortfall = (
  item: StockLowCheckInput | StockItem,
): { threshold: number; shortfall: number } => {
  const globalThreshold = item.reorderPoint ?? item.minThreshold
  let bestThreshold = typeof globalThreshold === 'number' && Number.isFinite(globalThreshold) ? globalThreshold : 0
  let bestShortfall =
    typeof globalThreshold === 'number' && Number.isFinite(globalThreshold)
      ? globalThreshold - item.quantity
      : Number.NEGATIVE_INFINITY

  const points = item.reorderPointByLocation
  if (points && typeof points === 'object') {
    for (const [loc, point] of Object.entries(points)) {
      if (typeof point === 'number' && Number.isFinite(point)) {
        const qtyAtLoc = item.byLocation?.[loc]
        const numericQty = typeof qtyAtLoc === 'number' && Number.isFinite(qtyAtLoc) ? qtyAtLoc : 0
        if (numericQty <= point) {
          const locShortfall = point - numericQty
          if (locShortfall > bestShortfall) {
            bestShortfall = locShortfall
            bestThreshold = point
          }
        }
      }
    }
  }

  return {
    threshold: bestThreshold,
    shortfall: Number.isFinite(bestShortfall) ? bestShortfall : 0,
  }
}
